/** ToolsTabWidget — read-only view of an agent's tools and MCPs.
 * Replaces components/settings/agent/ToolsTab.tsx.
 */

import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/ui/collapsible';
import { useAgentToolsQuery } from '../../../entities/agent/model/useAgentToolsQuery';

const triggerClassName =
  'w-full rounded-md px-1 py-1.5 text-left text-sm font-bold hover:bg-accent';

export default function ToolsTabWidget() {
  const { agentId = '' } = useParams<{ agentId: string }>();
  const { data, isLoading, error } = useAgentToolsQuery(agentId);

  return (
    <Collapsible defaultOpen>
      <CollapsibleTrigger className={triggerClassName}>Agent tools &amp; MCPs</CollapsibleTrigger>
      <CollapsibleContent>
        <div className="flex flex-col gap-2 pt-2">
          {isLoading && (
            <Loader2 role="progressbar" aria-label="Loading tools" className="size-5 animate-spin" />
          )}
          {error && (
            <p className="text-sm text-destructive">
              {error instanceof Error ? error.message : 'Failed to load tools'}
            </p>
          )}
          {data && (
            <>
              <p className="mt-2 text-sm font-bold">Tools ({data.tools.length})</p>
              {data.tools.length === 0 ? (
                <p className="text-sm text-muted-foreground">No tools declared.</p>
              ) : (
                <ul aria-label="Tools" className="flex flex-col gap-1">
                  {data.tools.map((t) => (
                    <li key={t.name} className="rounded-md px-2 py-1.5">
                      <p className="text-sm font-medium">{t.name}</p>
                      {t.description && <p className="text-xs text-muted-foreground">{t.description}</p>}
                    </li>
                  ))}
                </ul>
              )}

              <p className="mt-2 text-sm font-bold">MCPs ({data.mcps.length})</p>
              {data.mcps.length === 0 ? (
                <p className="text-sm text-muted-foreground">No MCPs declared.</p>
              ) : (
                data.mcps.map((m) => (
                  <Collapsible key={m.name} defaultOpen>
                    <CollapsibleTrigger className={triggerClassName}>
                      {m.name}
                      {m.tools.length > 0 ? ` (${m.tools.length} tools)` : ''}
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="flex flex-col gap-2 pt-2">
                        {m.description && (
                          <p className="text-sm text-muted-foreground">{m.description}</p>
                        )}
                        {m.tools.length === 0 ? (
                          <p className="text-sm text-muted-foreground">
                            No tools listed (external or uninspectable MCP).
                          </p>
                        ) : (
                          <ul aria-label={`${m.name} tools`} className="flex flex-col gap-1">
                            {m.tools.map((t) => (
                              <li key={t.name} className="rounded-md px-2 py-1.5">
                                <p className="text-sm font-medium">{t.name}</p>
                                {t.description && (
                                  <p className="text-xs text-muted-foreground">{t.description}</p>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                ))
              )}
            </>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
